import type { Routes } from '@angular/router';
import { authGuard } from '@core/auth/guards/auth.guard';
import { adminGuard } from '@core/auth/guards/admin.guard';
import { AdminShellComponent } from '@shared/layout';

export const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES),
  },
  {
    path: '',
    component: AdminShellComponent,
    canActivate: [authGuard, adminGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(m => m.DASHBOARD_ROUTES),
      },
      {
        path: 'finance',
        loadComponent: () =>
          import('./features/dashboard/pages/platform-finance-page.component').then(
            m => m.PlatformFinancePageComponent
          ),
      },
      {
        path: 'reports',
        loadChildren: () => import('./features/reports/reports.routes').then(m => m.REPORTS_ROUTES),
      },
      {
        path: 'reviews',
        loadChildren: () => import('./features/reviews/reviews.routes').then(m => m.REVIEWS_ROUTES),
      },
      {
        path: 'items',
        loadChildren: () => import('./features/items/items.routes').then(m => m.ITEMS_ROUTES),
      },
      {
        path: 'users',
        loadChildren: () => import('./features/users/users.routes').then(m => m.USERS_ROUTES),
      },
      {
        path: 'seller-requests',
        loadChildren: () =>
          import('./features/seller-requests/seller-requests.routes').then(
            m => m.SELLER_REQUESTS_ROUTES
          ),
      },
      {
        path: 'product-requests',
        loadChildren: () =>
          import('./features/product-requests/product-requests.routes').then(
            m => m.PRODUCT_REQUESTS_ROUTES
          ),
      },
      {
        path: 'shops',
        loadChildren: () => import('./features/shops/shops.routes').then(m => m.SHOPS_ROUTES),
      },
      {
        path: 'buyer-strikes',
        loadChildren: () =>
          import('./features/buyer-strikes/buyer-strikes.routes').then(m => m.BUYER_STRIKES_ROUTES),
      },
      {
        path: 'seller-debts',
        loadChildren: () =>
          import('./features/seller-debts/seller-debts.routes').then(m => m.SELLER_DEBTS_ROUTES),
      },
      {
        path: 'orders',
        loadChildren: () => import('./features/orders/orders.routes').then(m => m.ORDERS_ROUTES),
      },
      {
        path: 'missing-invoices',
        loadChildren: () =>
          import('./features/missing-invoices/missing-invoices.routes').then(
            m => m.MISSING_INVOICES_ROUTES
          ),
      },
      {
        path: 'lives',
        loadChildren: () => import('./features/lives/lives.routes').then(m => m.LIVES_ROUTES),
      },
      {
        path: 'discounts',
        loadChildren: () =>
          import('./features/discounts/discounts.routes').then(m => m.DISCOUNTS_ROUTES),
      },
      {
        path: 'referral-campaigns',
        loadChildren: () =>
          import('./features/referral-campaigns/referral-campaigns.routes').then(
            m => m.REFERRAL_CAMPAIGNS_ROUTES
          ),
      },
      {
        path: 'categories',
        loadChildren: () =>
          import('./features/categories/categories.routes').then(m => m.CATEGORIES_ROUTES),
      },
      {
        path: 'delivery-carriers',
        loadChildren: () =>
          import('./features/delivery-carriers/delivery-carriers.routes').then(
            m => m.DELIVERY_CARRIERS_ROUTES
          ),
      },
      {
        path: 'search',
        loadChildren: () => import('./features/search/search.routes').then(m => m.SEARCH_ROUTES),
      },
      {
        path: 'reconciliation',
        loadChildren: () =>
          import('./features/reconciliation/reconciliation.routes').then(
            m => m.RECONCILIATION_ROUTES
          ),
      },
      {
        path: 'messages',
        loadChildren: () =>
          import('./features/messages/messages.routes').then(m => m.MESSAGES_ROUTES),
      },
      {
        path: 'queues',
        loadChildren: () => import('./features/queues/queues.routes').then(m => m.QUEUES_ROUTES),
      },
      {
        path: 'admin-actions',
        loadChildren: () =>
          import('./features/admin-actions/admin-actions.routes').then(m => m.ADMIN_ACTIONS_ROUTES),
      },
      {
        path: 'nodes',
        loadChildren: () => import('./features/nodes/nodes.routes').then(m => m.NODES_ROUTES),
      },
      {
        path: 'system',
        loadChildren: () => import('./features/system/system.routes').then(m => m.SYSTEM_ROUTES),
      },
      {
        path: 'launch-status',
        loadChildren: () =>
          import('./features/launch-status/launch-status.routes').then(m => m.LAUNCH_STATUS_ROUTES),
      },
      {
        path: 'infra',
        loadChildren: () =>
          import('./features/infrastructure/infrastructure.routes').then(
            m => m.INFRASTRUCTURE_ROUTES
          ),
      },
      {
        path: 'emails',
        loadChildren: () => import('./features/emails/emails.routes').then(m => m.EMAILS_ROUTES),
      },
      {
        path: 'notification-deliveries',
        loadChildren: () =>
          import('./features/notification-deliveries/notification-deliveries.routes').then(
            m => m.NOTIFICATION_DELIVERIES_ROUTES
          ),
      },
      {
        path: 'support',
        loadChildren: () => import('./features/support/support.routes').then(m => m.SUPPORT_ROUTES),
      },
      // Backward-compatible redirect for the removed contact-requests feature
      { path: 'contact-requests', redirectTo: 'support', pathMatch: 'full' },
      {
        path: 'tcg-reference',
        loadChildren: () =>
          import('./features/tcg-reference/tcg-reference.routes').then(m => m.TCG_REFERENCE_ROUTES),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./features/settings/settings.routes').then(m => m.SETTINGS_ROUTES),
      },
      // Backward-compatible redirects for old routes
      { path: 'comments', redirectTo: 'reports', pathMatch: 'full' },
      { path: 'live-messages', redirectTo: 'reports', pathMatch: 'full' },
      // Backward-compatible redirects after the moderation section refonte
      { path: 'moderation', redirectTo: 'reports', pathMatch: 'full' },
      { path: 'moderation/flagged', redirectTo: 'reports', pathMatch: 'full' },
      { path: 'moderation/conversations', redirectTo: 'messages', pathMatch: 'full' },
      { path: 'moderation/conversations/:conversationId', redirectTo: 'messages/:conversationId' },
      { path: 'moderation/search', redirectTo: 'messages', pathMatch: 'full' },
      { path: '**', redirectTo: 'dashboard' },
    ],
  },
];
